import React, { useRef, useState } from 'react';
import { Database, Download, RefreshCw } from 'lucide-react';
import { ProjectFile } from '../../types';
import {
  downloadFileContentFromStorage,
  formatBytes,
  LARGE_FILE_THRESHOLD_BYTES,
  updateFile,
  uploadFileContentToStorage,
} from '../../services/projects';

interface LargeFilePlaceholderProps {
  file: ProjectFile;
  projectId: string;
  canEdit: boolean;
  onFileUpdated: (updated: ProjectFile) => void;
}

export function LargeFilePlaceholder({
  file,
  projectId,
  canEdit,
  onFileUpdated,
}: LargeFilePlaceholderProps) {
  const [downloading, setDownloading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  async function handleDownload() {
    if (!file.storageContentPath) return;
    setDownloading(true);
    try {
      const content = await downloadFileContentFromStorage(file.storageContentPath);
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloading(false);
    }
  }

  async function handleReplace(event: React.ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0];
    if (!selected) return;
    setReplacing(true);
    try {
      const buffer = await selected.arrayBuffer();

      if (buffer.byteLength > LARGE_FILE_THRESHOLD_BYTES) {
        const storagePath = await uploadFileContentToStorage(projectId, file.id, buffer);
        await updateFile(projectId, file.id, {
          storageContentPath: storagePath,
          storageSizeBytes: buffer.byteLength,
        });
        onFileUpdated({
          ...file,
          storageContentPath: storagePath,
          storageSizeBytes: buffer.byteLength,
        });
      } else {
        // Replacement is small enough to go inline — clear storage fields
        const content = new TextDecoder().decode(buffer);
        await updateFile(projectId, file.id, {
          content,
          storageContentPath: '' as unknown as undefined,
          storageSizeBytes: 0 as unknown as undefined,
          otEnabled: true,
        });
        onFileUpdated({
          ...file,
          content,
          storageContentPath: undefined,
          storageSizeBytes: undefined,
          otEnabled: true,
        });
      }
    } catch (error) {
      console.error('Failed to replace file:', error);
    } finally {
      setReplacing(false);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  }

  const sizeLabel = file.storageSizeBytes != null
    ? formatBytes(file.storageSizeBytes)
    : 'Large file';

  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 p-8">
      <div className="flex flex-col items-center gap-4 rounded-xl border border-gray-200 bg-white px-10 py-8 shadow-sm">
        <Database className="h-10 w-10 text-amber-500" />
        <div className="text-center">
          <div className="text-sm font-medium text-gray-900">{file.name}</div>
          <div className="mt-1 text-xs text-gray-500">{sizeLabel} — stored in Cloud Storage</div>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Downloading...' : 'Download'}
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => replaceInputRef.current?.click()}
                disabled={replacing}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${replacing ? 'animate-spin' : ''}`} />
                {replacing ? 'Replacing...' : 'Replace'}
              </button>
              <input
                ref={replaceInputRef}
                type="file"
                onChange={handleReplace}
                className="hidden"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
